import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../store';
import {
  setDogPoses,
  setDistinctDogStates,
  setDogPoseFrequency,
  setDogPoseDuration,
  setPreviousDogPoseFrequency,
  setPreviousDogPoseDuration,
  clearDogPoses,
  setCurrentSession,
  DashboardSession,
} from '../../store/slices/dashboardSlice';
import {
  fetchProfiles,
  saveProfile,
} from '../../store/slices/alertConfigSlice';
import type { AlertConfigProfile } from '../../services/alertConfigService';
import { dashboardService } from '../../services/dashboard';
import { alertLogService } from '../../services/alertLogService';
import { i18nService } from '../../services/i18n';
import { DogPoseDataPoint } from '../../store/slices/dashboardSlice';
import {
  TimeRangePreset,
  ComparisonPeriod,
  getTimeRange,
  getComparisonPeriod,
  getDefaultTimezone,
} from '../../utils/timezone';
import BehaviorSummary, { type BehaviorStats, type ComparisonMode, type SummaryView } from './BehaviorSummary';
import { convertPosesToGanttBars } from './poseConverter';
import type { GanttBarData } from './behaviorTypes';
import TimeRangeSelector, { type CustomTimeRange } from './TimeRangeSelector';

declare global {
  interface Window {
    __APP_MODE__?: 'dev' | 'demo';
  }
}

const isDemoMode = (): boolean => typeof window !== 'undefined' && window.__APP_MODE__ === 'demo';

// Import Phase 1 UI components
import PostureMonitor from './PostureMonitor';
import SessionControls from './SessionControls';
import BehaviorTimelineV2 from './BehaviorTimelineV2';
import BehaviorFrequencyChart from './BehaviorFrequencyChart';
import AlertConfigPanel from './AlertConfigPanel';
import { type Contact } from './ContactManager';
import AlertHistoryPanel, { AlertLogEntry } from './AlertHistoryPanel';
import AlertReportModal from './AlertReportModal';
import { AlertContainer, AlertData } from './AlertNotification';
import {
  ThresholdConfig,
  DEFAULT_THRESHOLDS,
} from './behaviorTypes';
import { evaluateAlerts } from './alertEvaluator';

interface SequenceDetectViewProps {
  onNavigateHome?: () => void;
}

const SequenceDetectView: React.FC<SequenceDetectViewProps> = ({ onNavigateHome }) => {
  const dispatch = useDispatch<AppDispatch>();

  // Redux state for dog pose data
  const dogPoses = useSelector((state: RootState) => state.dashboard.dogPoses);
  const distinctDogStates = useSelector((state: RootState) => state.dashboard.distinctDogStates);
  const dogPoseFrequency = useSelector((state: RootState) => state.dashboard.dogPoseFrequency);
  const dogPoseDuration = useSelector((state: RootState) => state.dashboard.dogPoseDuration);
  const previousDogPoseFrequency = useSelector((state: RootState) => state.dashboard.previousDogPoseFrequency);
  const previousDogPoseDuration = useSelector((state: RootState) => state.dashboard.previousDogPoseDuration);
  const currentSession = useSelector((state: RootState) => state.dashboard.currentSession);
  const isDashboardConnected = useSelector((state: RootState) => state.dashboard.isConnected);

  // Time range, timezone, and comparison state
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRangePreset>('24h');
  const [customTimeRange, setCustomTimeRange] = useState<CustomTimeRange | null>(null);
  const [selectedTimezone] = useState<string>(getDefaultTimezone());
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('threshold');
  const [comparisonPeriod, setComparisonPeriod] = useState<ComparisonPeriod['key']>('yesterday');
  const [summaryView, setSummaryView] = useState<SummaryView>('frequency');
  const [lastDataRefresh, setLastDataRefresh] = useState<number>(Date.now());
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = React.useRef<HTMLDivElement>(null);
  // Export time range filter (epoch ms, null = no filter)
  const [exportFrom, setExportFrom] = useState<number | null>(null);
  const [exportTo, setExportTo] = useState<number | null>(null);
  // Export result message (shown as a toast)
  const [exportMessage, setExportMessage] = useState<{ text: string; type: 'success' | 'warning' } | null>(null);

  // Auto-dismiss export message after timeout
  useEffect(() => {
    if (!exportMessage) return;
    const timeout = exportMessage.type === 'success' ? 3000 : 5000;
    const timer = setTimeout(() => setExportMessage(null), timeout);
    return () => clearTimeout(timer);
  }, [exportMessage]);

  // Close export dropdown when clicking outside
  useEffect(() => {
    if (!showExportMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);
  const computedStats = useMemo<BehaviorStats>(() => {
    // Map dog_state keys to behavior stats
    // DB frequency keys are dog_state values: sit, stand, lie, walk, scratch, unsure
    // DB duration keys are the same, values are already in minutes
    const freq = dogPoseFrequency;
    const dur = dogPoseDuration;

    const scratchCount = freq['scratch'] ?? 0;
    const walkCount = freq['walk'] ?? 0;
    const sittingCount = freq['sit'] ?? 0;
    const standingCount = freq['stand'] ?? 0;
    const lyingCount = freq['lie'] ?? 0;
    const unknownCount = freq['unsure'] ?? 0;

    // Duration values are already in minutes from getPosesDuration
    const lyingDuration = dur['lie'] ?? 0;
    const sittingDuration = dur['sit'] ?? 0;
    const standingDuration = dur['stand'] ?? 0;

    const totalEvents = scratchCount + walkCount + sittingCount + standingCount + lyingCount + unknownCount;

    return {
      scratchCount,
      walkCount,
      lyingDuration: Math.round(lyingDuration * 10) / 10,
      sittingDuration: Math.round(sittingDuration * 10) / 10,
      standingDuration: Math.round(standingDuration * 10) / 10,
      lyingCount,
      sittingCount,
      standingCount,
      totalEvents,
    };
  }, [dogPoseFrequency, dogPoseDuration]);

  // Compute previous period stats for trend comparison
  const previousStats = useMemo<BehaviorStats | null>(() => {
    const freq = previousDogPoseFrequency;
    const dur = previousDogPoseDuration;

    if (Object.keys(freq).length === 0 && Object.keys(dur).length === 0) {
      return null;
    }

    const scratchCount = freq['scratch'] ?? 0;
    const walkCount = freq['walk'] ?? 0;
    const lyingDuration = dur['lie'] ?? 0;
    const sittingDuration = dur['sit'] ?? 0;
    const standingDuration = dur['stand'] ?? 0;
    const totalEvents = Object.values(freq).reduce((sum, v) => sum + v, 0);

    return {
      scratchCount,
      walkCount,
      lyingDuration: Math.round(lyingDuration * 10) / 10,
      sittingDuration: Math.round(sittingDuration * 10) / 10,
      standingDuration: Math.round(standingDuration * 10) / 10,
      lyingCount: freq['lie'] ?? 0,
      sittingCount: freq['sit'] ?? 0,
      standingCount: freq['stand'] ?? 0,
      totalEvents,
    };
  }, [previousDogPoseFrequency, previousDogPoseDuration]);

  // Current behavior for PostureMonitor
  const [currentBehavior] = useState<string>('Unknown');
  const [confidence] = useState<number>(0);

  // Threshold config - use new ThresholdConfig structure
  const [thresholds, setThresholds] = useState<ThresholdConfig>(DEFAULT_THRESHOLDS);

  // Alert config profiles from Redux
  const alertConfigProfiles = useSelector((state: RootState) => state.alertConfig.profiles);
  const activeProfileId = useSelector((state: RootState) => state.alertConfig.activeProfileId);
  const activeProfile = useMemo(
    () => alertConfigProfiles.find((p: AlertConfigProfile) => p.id === activeProfileId) ?? null,
    [alertConfigProfiles, activeProfileId]
  );

  // Sync thresholds from active profile
  useEffect(() => {
    if (activeProfile) {
      setThresholds(activeProfile.thresholds);
    }
  }, [activeProfile]);

  // Detail mode: true = show every detection (no merge), false = merge nearby same-type bars
  const [detailMode, setDetailMode] = useState(true);

  // Brush selection state from BehaviorTimelineV2 (epoch ms)
  const [brushStart, setBrushStart] = useState<number | null>(null);
  const [brushEnd, setBrushEnd] = useState<number | null>(null);

  // Convert dog poses from Redux into GanttBarData[] for the timeline
  // mergeGapMs comes from the user-configurable merge gap (stored in seconds, converted to ms)
  const ganttBars = useMemo<GanttBarData[]>(() => {
    if (dogPoses.length === 0) return [];
    const mergeGapMs = thresholds.mergeGapSec * 1000;
    return convertPosesToGanttBars(dogPoses, thresholds, !detailMode, mergeGapMs);
  }, [dogPoses, thresholds, detailMode]);

  // Contacts
  const [contacts] = useState<Contact[]>([
    {
      id: '1',
      name: 'John Owner',
      email: 'john@example.com',
      phone: '+1-555-0123',
      role: 'owner',
      isPrimary: true
    },
    {
      id: '2',
      name: 'Dr. Sarah Vet',
      email: 'vet@clinic.com',
      role: 'vet',
      isPrimary: false
    }
  ]);
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(['1']);

  // Modal states
  const [showAlertConfig, setShowAlertConfig] = useState(false);

  // Alerts
  const [alerts, setAlerts] = useState<AlertData[]>([]);

  // Alert History — computed from gantt bars and thresholds.
  // Uses the user-selected time range (preset or custom) so frequency/duration
  // normalization matches the window the user is viewing. Recalculates when the
  // time range or thresholds change — this is the core of simulated mode.
  const alertHistory = useMemo<AlertLogEntry[]>(() => {
    if (ganttBars.length === 0) return [];

    // Resolve the effective time range the same way the data fetch does (line ~300),
    // so simulated alerts are normalized over the same window as the charts/summary.
    const effectiveRange = customTimeRange ?? getTimeRange(selectedTimeRange, selectedTimezone);
    const timeRangeStartMs = effectiveRange.from;
    const timeRangeEndMs = effectiveRange.to;

    if (timeRangeEndMs <= timeRangeStartMs) return [];

    return evaluateAlerts({
      ganttBars,
      thresholds,
      timeRangeStartMs,
      timeRangeEndMs,
    });
  }, [ganttBars, thresholds, selectedTimeRange, customTimeRange, selectedTimezone]);

  // Selected alert for report modal
  const [selectedAlert, setSelectedAlert] = useState<AlertLogEntry | null>(null);

  // ─── Phase 3: Persisted alert history (Mode 2) ──────────────────────────────
  const [persistedAlerts, setPersistedAlerts] = useState<AlertLogEntry[]>([]);
  const [isLoadingPersisted, setIsLoadingPersisted] = useState(false);
  const [viewMode, setViewMode] = useState<'persisted' | 'simulated'>('persisted');

  // Load persisted alerts from DB when session changes or connection establishes
  useEffect(() => {
    const sessionId = currentSession?.id ?? dashboardService.getCurrentSessionId();
    if (!sessionId || !isDashboardConnected) {
      setPersistedAlerts([]);
      return;
    }
    let cancelled = false;
    setIsLoadingPersisted(true);
    alertLogService.listAlertLogs({ sessionId, limit: 200 })
      .then((logs) => {
        if (!cancelled) setPersistedAlerts(logs);
      })
      .catch((err) => console.error('[SequenceDetectView] Failed to load alert logs:', err))
      .finally(() => { if (!cancelled) setIsLoadingPersisted(false); });
    return () => { cancelled = true; };
  }, [currentSession?.id, isDashboardConnected]);

  // Connect to dashboard service on mount, then fetch alert config profiles
  useEffect(() => {
    dashboardService.init();
    dashboardService.connect().then(() => {
      dispatch(fetchProfiles());
    }).catch((err) => {
      console.error('[SequenceDetectView] Auto-connect failed:', err);
    });
  }, [dispatch]);

  // Fetch dog pose data from DB when a session is active
  useEffect(() => {
    // Use Redux session ID first, then fall back to dashboard service's tracked session
    const sessionId = currentSession?.id ?? dashboardService.getCurrentSessionId();
    if (!sessionId) {
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setIsDataLoading(true);
      try {
        const timeRange = customTimeRange ?? getTimeRange(selectedTimeRange, selectedTimezone);

        const [poses, states, frequency, duration] = await Promise.all([
          dashboardService.getDogPoses(sessionId!, timeRange.from, timeRange.to, 10000),
          dashboardService.getDistinctDogStates(sessionId!),
          dashboardService.getPosesFrequency(sessionId!, timeRange.from, timeRange.to),
          dashboardService.getPosesDuration(sessionId!, timeRange.from, timeRange.to),
        ]);

        if (!cancelled) {
          if (poses.length > 0) {
            dispatch(setDogPoses(poses as DogPoseDataPoint[]));
          }
          if (states.length > 0) {
            dispatch(setDistinctDogStates(states));
          }
          dispatch(setDogPoseFrequency(frequency));
          dispatch(setDogPoseDuration(duration));
          setLastDataRefresh(Date.now());
        }
      } catch (err) {
        console.error('[SequenceDetectView] Failed to fetch dog pose data:', err);
      } finally {
        if (!cancelled) {
          setIsDataLoading(false);
        }
      }
    };

    // Fetch previous period data for trend comparison
    const fetchPreviousData = async () => {
      if (comparisonMode !== 'trend') return;

      const sessionId = currentSession?.id ?? dashboardService.getCurrentSessionId();
      if (!sessionId) return;

      try {
        const timeRange = customTimeRange
          ? { preset: selectedTimeRange, from: customTimeRange.from, to: customTimeRange.to, label: 'Custom' }
          : getTimeRange(selectedTimeRange, selectedTimezone);
        const compPeriod = getComparisonPeriod(comparisonPeriod, timeRange);

        const [prevFreq, prevDur] = await Promise.all([
          dashboardService.getPosesFrequency(sessionId, compPeriod.from, compPeriod.to),
          dashboardService.getPosesDuration(sessionId, compPeriod.from, compPeriod.to),
        ]);

        if (!cancelled) {
          dispatch(setPreviousDogPoseFrequency(prevFreq));
          dispatch(setPreviousDogPoseDuration(prevDur));
        }
      } catch (err) {
        console.error('[SequenceDetectView] Failed to fetch previous period data:', err);
      }
    };

    fetchData();
    fetchPreviousData();

    // Refresh data every 30 seconds
    const interval = setInterval(() => {
      fetchData();
      fetchPreviousData();
    }, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [currentSession?.id, isDashboardConnected, selectedTimeRange, customTimeRange, selectedTimezone, comparisonMode, comparisonPeriod, dispatch]);

  // Clear dog pose data when no session is active
  useEffect(() => {
    const sessionId = currentSession?.id ?? dashboardService.getCurrentSessionId();
    if (!sessionId) {
      dispatch(clearDogPoses());
    }
  }, [currentSession?.id, dispatch]);

  // Dismiss alert
  const handleDismissAlert = useCallback((id: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  }, []);

  // Save thresholds
  const handleSaveThresholds = useCallback((newThresholds: ThresholdConfig) => {
    setThresholds(newThresholds);
    // Auto-save to active profile if one exists
    if (activeProfile) {
      dispatch(saveProfile({
        id: activeProfile.id,
        name: activeProfile.name,
        thresholds: newThresholds,
        filters: activeProfile.filters,
        activityDrop: newThresholds.activityDrop,
        mergeGapSec: newThresholds.mergeGapSec,
        isDefault: activeProfile.isDefault,
      }));
    }
  }, [activeProfile, dispatch]);

  // Save selected contacts for alerts
  const handleSaveSelectedContacts = useCallback((contactIds: string[]) => {
    setSelectedContactIds(contactIds);
  }, []);

  // Handle view alert report
  const handleViewAlertReport = useCallback((alert: AlertLogEntry) => {
    setSelectedAlert(alert);
  }, []);

  // Handle acknowledge alert — since alertHistory is now computed,
  // we track acknowledged IDs separately
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(new Set());

  const handleAcknowledgeAlert = useCallback((id: string) => {
    setAcknowledgedIds(prev => new Set(prev).add(id));
  }, []);

  // Merge acknowledged state into computed alerts
  const alertHistoryWithAck = useMemo<AlertLogEntry[]>(() => {
    return alertHistory.map(alert => ({
      ...alert,
      acknowledged: acknowledgedIds.has(alert.id) || alert.acknowledged,
    }));
  }, [alertHistory, acknowledgedIds]);

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-claude-darkBg">
      {/* Header — the shared bar (h-12, px-4, border-b), the same "Back" control and title size as the device views */}
      <div className="draggable flex h-12 items-center justify-between px-4 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <div className="flex items-center justify-between">
          <div className="non-draggable flex items-center gap-3">
            <button
              onClick={onNavigateHome}
              className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
              {i18nService.t('sequenceDetect')}
            </h1>
          </div>

          {/* Action buttons */}
          <div className="ml-4 flex items-center gap-2">
            {/* Export dropdown */}
            <div className="relative" ref={exportMenuRef}>
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={isExporting || (!selectedSessionId && !currentSession?.id)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-claude-darkSurface border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Export dog poses"
              >
                {isExporting ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                )}
                {isExporting ? 'Exporting...' : 'Export'}
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showExportMenu && !isExporting && (
                <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-claude-darkSurface border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden min-w-[320px]">
                  {/* Time range filter */}
                  <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Time Range Filter</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 dark:text-gray-400 w-8 shrink-0">From</label>
                        <input
                          type="datetime-local"
                          value={exportFrom != null ? new Date(exportFrom).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            if (e.target.value) {
                              setExportFrom(new Date(e.target.value).getTime());
                            } else {
                              setExportFrom(null);
                            }
                          }}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-gray-500 dark:text-gray-400 w-8 shrink-0">To</label>
                        <input
                          type="datetime-local"
                          value={exportTo != null ? new Date(exportTo).toISOString().slice(0, 16) : ''}
                          onChange={(e) => {
                            if (e.target.value) {
                              setExportTo(new Date(e.target.value).getTime());
                            } else {
                              setExportTo(null);
                            }
                          }}
                          className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <button
                          onClick={() => {
                            if (brushStart != null && brushEnd != null) {
                              setExportFrom(brushStart);
                              setExportTo(brushEnd);
                            }
                          }}
                          disabled={brushStart == null || brushEnd == null}
                          className={`px-2 py-0.5 text-xs font-medium rounded transition-colors ${
                            brushStart != null && brushEnd != null
                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-800 cursor-pointer'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed'
                          }`}
                          title={brushStart != null && brushEnd != null ? 'Fill from timeline brush selection' : 'Interact with the timeline brush first'}
                        >
                          Use brush range
                        </button>
                        <button
                          onClick={() => {
                            const tr = getTimeRange(selectedTimeRange, selectedTimezone);
                            setExportFrom(tr.from);
                            setExportTo(tr.to);
                          }}
                          className="px-2 py-0.5 text-xs font-medium rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-800 transition-colors"
                        >
                          Use time range
                        </button>
                        {(exportFrom != null || exportTo != null) && (
                          <button
                            onClick={() => {
                              setExportFrom(null);
                              setExportTo(null);
                            }}
                            className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500">
                        {exportFrom == null && exportTo == null
                          ? 'No filter — exports all session data'
                          : exportFrom != null && exportTo != null
                            ? `Filtering: ${new Date(exportFrom).toLocaleString()} → ${new Date(exportTo).toLocaleString()}`
                            : exportFrom != null
                              ? `From: ${new Date(exportFrom).toLocaleString()}`
                              : `Until: ${new Date(exportTo!).toLocaleString()}`
                        }
                      </p>
                    </div>
                  </div>
                  {/* Export buttons */}
                  <button
                    onClick={async () => {
                      setShowExportMenu(false);
                      const sessionId = selectedSessionId ?? currentSession?.id ?? dashboardService.getCurrentSessionId();
                      if (!sessionId) return;
                      setIsExporting(true);
                      try {
                        const result = await dashboardService.exportDogPosesCsv(sessionId, exportFrom ?? undefined, exportTo ?? undefined);
                        if (result.success) {
                          setExportMessage({ text: `Exported ${result.count.toLocaleString()} records as CSV`, type: 'success' });
                        } else {
                          setExportMessage({ text: 'No data found in the selected time range. Try adjusting the time range or brush selection.', type: 'warning' });
                        }
                      } catch {
                        setExportMessage({ text: 'Export failed. Please try again.', type: 'warning' });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    Export as CSV
                  </button>
                  <button
                    onClick={async () => {
                      setShowExportMenu(false);
                      const sessionId = selectedSessionId ?? currentSession?.id ?? dashboardService.getCurrentSessionId();
                      if (!sessionId) return;
                      setIsExporting(true);
                      try {
                        const result = await dashboardService.exportDogPosesXlsx(sessionId, exportFrom ?? undefined, exportTo ?? undefined);
                        if (result.success) {
                          setExportMessage({ text: `Exported ${result.count.toLocaleString()} records as XLSX`, type: 'success' });
                        } else {
                          setExportMessage({ text: 'No data found in the selected time range. Try adjusting the time range or brush selection.', type: 'warning' });
                        }
                      } catch {
                        setExportMessage({ text: 'Export failed. Please try again.', type: 'warning' });
                      } finally {
                        setIsExporting(false);
                      }
                    }}
                    className="w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    Export as XLSX
                  </button>
                </div>
              )}
            </div>

            {/* Export result message toast */}
            {exportMessage && (
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-all ${
                  exportMessage.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800'
                    : 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800'
                }`}
              >
                {exportMessage.type === 'success' ? (
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                )}
                <span>{exportMessage.text}</span>
                <button
                  onClick={() => setExportMessage(null)}
                  className="ml-1 text-current opacity-50 hover:opacity-100 transition-opacity"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {!isDemoMode() && (
            <button
              onClick={() => setShowAlertConfig(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-claude-darkSurface border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.559.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.397-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.149-.894z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Configure Alerts
            </button>
            )}


          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Top Section: Camera + Controls */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Camera / Posture Monitor */}
            <div className="lg:col-span-2">
              <PostureMonitor
                isMonitoring={!!currentSession}
                currentBehavior={currentBehavior}
                confidence={confidence}
              />
            </div>

            {/* Session Controls */}
            <div className="space-y-6">
              <SessionControls
                selectedSessionId={selectedSessionId}
                onSelectSession={(sessionId) => {
                  setSelectedSessionId(sessionId);
                  // Create a minimal session object for the Redux state
                  const session: DashboardSession = {
                    id: sessionId,
                    cameraName: 'Historical Session',
                    cameraUrl: '',
                    status: 'stopped',
                    startedAt: null,
                    endedAt: null,
                  };
                  dispatch(setCurrentSession(session));
                }}
                onClearSelection={() => {
                  setSelectedSessionId(null);
                  dispatch(setCurrentSession(null));
                }}
              />

              {/* Quick Stats */}
              <div className="rounded-xl bg-white dark:bg-claude-darkSurface p-4 border border-claude-border dark:border-claude-darkBorder">
                <h3 className="text-sm font-medium text-gray-700 dark:text-claude-darkText mb-3">
                  Session Stats
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Total Events</span>
                    <span className="font-medium text-gray-900 dark:text-claude-darkText">
                      {computedStats.totalEvents}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500 dark:text-gray-400">Alert Contacts</span>
                    <span className="font-medium text-gray-900 dark:text-claude-darkText">
                      {selectedContactIds.length}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Shared Time Range Selector (presets + custom date picker) */}
          <div className="mb-4">
            <TimeRangeSelector
              timeRange={selectedTimeRange}
              onTimeRangeChange={setSelectedTimeRange}
              timezone={selectedTimezone}
              comparisonPeriod={comparisonPeriod}
              onComparisonPeriodChange={setComparisonPeriod}
              showComparison={comparisonMode === 'trend'}
              customTimeRange={customTimeRange}
              onCustomTimeRangeChange={setCustomTimeRange}
            />
          </div>

          {/* Behavior Summary */}
          <BehaviorSummary
            stats={computedStats}
            previousStats={previousStats}
            thresholds={thresholds}
            timeRange={selectedTimeRange}
            timezone={selectedTimezone}
            comparisonMode={comparisonMode}
            comparisonPeriod={comparisonPeriod}
            summaryView={summaryView}
            onTimeRangeChange={setSelectedTimeRange}
            onComparisonModeChange={setComparisonMode}
            onComparisonPeriodChange={setComparisonPeriod}
            onSummaryViewChange={setSummaryView}
            lastUpdated={lastDataRefresh}
            isLoading={isDataLoading}
          />

          {/* Behavior Frequency Chart */}
          <BehaviorFrequencyChart
            thresholds={thresholds}
            dogPoses={dogPoses}
            distinctDogStates={distinctDogStates}
            timeRange={selectedTimeRange}
            timezone={selectedTimezone}
            customTimeRange={customTimeRange}
            visibleLinesOverride={activeProfile?.filters}
            onVisibleLinesChange={(filters) => {
              if (activeProfile) {
                dispatch(saveProfile({
                  id: activeProfile.id,
                  name: activeProfile.name,
                  thresholds: activeProfile.thresholds,
                  filters,
                  activityDrop: activeProfile.activityDrop,
                  mergeGapSec: activeProfile.mergeGapSec,
                  isDefault: activeProfile.isDefault,
                }));
              }
            }}
          />



          {/* Behavior Timeline V2 (Floating Bar Chart) */}
          <BehaviorTimelineV2
            events={ganttBars}
            timeRange={selectedTimeRange}
            customTimeRange={customTimeRange}
            detailMode={detailMode}
            onDetailModeChange={setDetailMode}
            onBrushChange={(start, end) => {
              setBrushStart(start);
              setBrushEnd(end);
            }}
          />

          {/* Alert History Panel */}
          <AlertHistoryPanel
            alerts={viewMode === 'persisted' ? persistedAlerts : alertHistoryWithAck}
            onViewReport={handleViewAlertReport}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isLoading={isLoadingPersisted && viewMode === 'persisted'}
            hasData={ganttBars.length > 0}
          />
        </div>
      </div>

      {/* Modals */}
      {showAlertConfig && (
        <AlertConfigPanel
          thresholds={thresholds}
          onThresholdsChange={handleSaveThresholds}
          contacts={contacts}
          selectedContacts={selectedContactIds}
          onSelectedContactsChange={handleSaveSelectedContacts}
          onClose={() => setShowAlertConfig(false)}
        />
      )}

      {/* Alert Notifications */}
      <AlertContainer
        alerts={alerts}
        onDismiss={handleDismissAlert}
      />

      {/* Alert Report Modal */}
      {selectedAlert && (
        <AlertReportModal
          alert={selectedAlert}
          onClose={() => setSelectedAlert(null)}
          onAcknowledge={handleAcknowledgeAlert}
        />
      )}
    </div>
  );
};

export default SequenceDetectView;
